import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CreditCard, DollarSign, Star } from 'lucide-react';
import { BankLogo } from '../ui/bank-logo';
import { POPULAR_BANKS } from '../../utils/popularBanks';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  PaymentAccount,
  PaymentAccountFormData,
  PaymentAccountType,
  PaymentAccountStatus,
  formatPaymentAccountType,
  validateAccountNumber
} from '../../types/payment-account';

const formSchema = z.object({
  name: z.string()
    .min(2, 'Nome deve ter pelo menos 2 caracteres')
    .max(50, 'Nome deve ter no máximo 50 caracteres'),
  description: z.string().optional(),
  type: z.nativeEnum(PaymentAccountType, {
    required_error: 'Selecione um tipo'
  }),
  bank_name: z.string().optional(),
  account_number: z.string().optional(),
  agency: z.string().optional(),
  initial_balance: z.coerce.number().default(0),
  status: z.nativeEnum(PaymentAccountStatus).default(PaymentAccountStatus.ACTIVE),
  is_default: z.boolean().default(false)
});

interface PaymentAccountFormProps {
  account?: PaymentAccount | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: PaymentAccountFormData) => void;
}


export function PaymentAccountForm({ account, open, onOpenChange, onSubmit }: PaymentAccountFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!account;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      type: PaymentAccountType.CHECKING,
      bank_name: '',
      account_number: '',
      agency: '',
      initial_balance: 0,
      status: PaymentAccountStatus.ACTIVE,
      is_default: false
    }
  });

  const watchedName = form.watch('name');
  const watchedType = form.watch('type');
  const watchedBankName = form.watch('bank_name');
  const watchedIsDefault = form.watch('is_default');

  // Update form when account changes
  useEffect(() => {
    if (account) {
      form.reset({
        name: account.name,
        description: account.description || '',
        type: account.type,
        bank_name: account.bank_name || '',
        account_number: account.account_number || '',
        agency: account.agency || '',
        initial_balance: account.initial_balance,
        status: account.status,
        is_default: account.is_default
      });
    }
  }, [account, form]);

  const handleSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data as PaymentAccountFormData);
      if (!isEditing) {
        form.reset();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  const needsBankInfo = [
    PaymentAccountType.CHECKING,
    PaymentAccountType.SAVINGS,
    PaymentAccountType.CREDIT_CARD
  ].includes(watchedType);

  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {isEditing ? 'Editar Conta' : 'Nova Conta'}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Edite as informações da conta de pagamento' 
              : 'Crie uma nova conta de pagamento para suas transações'
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            {/* Preview */}
            {watchedName && (
              <div className="p-4 rounded-lg bg-muted/50 border border-dashed">
                <div className="flex items-center gap-3">
                  {watchedBankName ? (
                    <BankLogo
                      bankName={watchedBankName}
                      size="medium"
                      showFallback={true}
                    />
                  ) : (
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-muted0 text-white">
                        {watchedName.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{watchedName}</span>
                      {watchedIsDefault && (
                        <Star className="h-3 w-3 text-yellow-500 fill-current" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {formatPaymentAccountType(watchedType)}
                      </Badge>
                      {watchedBankName && (
                        <span className="text-xs text-muted-foreground">
                          {watchedBankName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-6 md:grid-cols-2">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm">Informações Básicas</h4>
                
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome da Conta *</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Ex: Conta Corrente Principal"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo *</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o tipo" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.values(PaymentAccountType).map(type => (
                            <SelectItem key={type} value={type}>
                              {formatPaymentAccountType(type)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="initial_balance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Saldo Inicial
                      </FormLabel>
                      <FormControl>
                        <Input 
                          type="number"
                          step="0.01"
                          placeholder="0,00"
                          {...field}
                          onChange={e => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormDescription>
                        Saldo atual da conta (pode ser positivo ou negativo)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descrição</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Descrição opcional da conta..."
                          className="min-h-[60px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Bank and Account Details */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm">Detalhes da Conta</h4>

                {needsBankInfo && (
                  <>
                    <FormField
                      control={form.control}
                      name="bank_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Banco</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione o banco" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {POPULAR_BANKS.map(bank => (
                                <SelectItem key={bank} value={bank}>
                                  <div className="flex items-center gap-2">
                                    <BankLogo bankName={bank} size="small" />
                                    <span>{bank}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="agency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Agência</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="1234-5"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="account_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Número da Conta</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="12345-6"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}

                
                {isEditing && (
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value={PaymentAccountStatus.ACTIVE}>
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500" />
                                Ativa
                              </div>
                            </SelectItem>
                            <SelectItem value={PaymentAccountStatus.INACTIVE}>
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-muted0" />
                                Inativa
                              </div>
                            </SelectItem>
                            <SelectItem value={PaymentAccountStatus.BLOCKED}>
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-red-500" />
                                Bloqueada
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="is_default"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          Definir como conta padrão
                        </FormLabel>
                        <FormDescription>
                          Esta conta será selecionada automaticamente em novas transações
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4" />
                    {isEditing ? 'Salvar Alterações' : 'Criar Conta'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}